import SalutationInterface from '@/interfaces/SalutationInterface';
import {
  createRouter,
  createWebHistory,
} from 'vue-router';
import { EnetAPI, OrderAPI, IoOrderAPI } from '@/api';

import store from '@/store';

import BasePage from '@/pages/BasePage.vue';
import Priceshow from '@/pages/PriceshowPage.vue';

const beforeSearchRouteEnter = (): void => {
  store.dispatch('page/setStatus', 1);
};

const fetchSuppliers = async () => EnetAPI.getAllSuppliers().then((suppliers) => {
  store.dispatch('order/setSuppliers', suppliers);
});

const fetchSalutations = async () => IoOrderAPI.getSalutations().then((salutations: { business: SalutationInterface[], private: SalutationInterface[]}) => {
  store.dispatch('order/setSalutations', salutations);
});

const fetchBillingTypes = async () => IoOrderAPI.getBillingTypes().then((billingTypes) => {
  store.dispatch('order/setBillingTypes', billingTypes);
});

const fetchFormData = async () => OrderAPI.getFormData().then((response) => {
  store.dispatch('order/setFormData', response.formData);
});

const fetchLastOrderedItem = async () => OrderAPI.getLastOrderedItem().then((response) => {
  store.dispatch('order/setLastOrderedItem', response.item);
});

const deleteLastOrderedItem = async () => OrderAPI.deleteLastOrderedItem();

const beforeOrderRouteEnter = async (): Promise<boolean> => {
  store.dispatch('page/setStatus', 1);
  const orderFormData = store.getters['order/formData'];
  if (!Object.keys(orderFormData.address).length) {
    await fetchFormData();
  }

  const hasSupplier = store.getters['order/hasSupplier']('power')
    && store.getters['order/hasSupplier']('gas');

  if (!hasSupplier) {
    await fetchSuppliers();
  }

  const hasSalutationsPrivate = store.getters['order/hasSalutations']('private');
  const hasSalutationsBusiness = store.getters['order/hasSalutations']('business');

  if (!(hasSalutationsPrivate && hasSalutationsBusiness)) {
    await fetchSalutations();
  }

  const hasBillingTypes = store.getters['order/hasBillingTypes'];

  if (!hasBillingTypes) {
    await fetchBillingTypes();
  }

  store.dispatch('page/setStatus', 0);

  return true;
};

const beforeOrderSuccessRouteEnter = async (): Promise<boolean> => {
  store.dispatch('page/setStatus', 1);

  let lastOrderedItem = store.getters['order/lastOrderedItem'];

  if (lastOrderedItem === null) {
    await fetchLastOrderedItem();
  }

  lastOrderedItem = store.getters['order/lastOrderedItem'];

  store.dispatch('page/setStatus', 0);

  if (lastOrderedItem !== null) {
    await deleteLastOrderedItem();

    return true;
  }

  return false;
};

const updateOrderPageMeta = (meta: Record<string, string>) => {
  const $title = document.querySelector('head title');
  const $description = document.querySelector('head [name="description"]');
  const $keywords = document.querySelector('head [name="keywords"]');

  if ($title) {
    $title.innerHTML = meta.title;
  }
  if ($description) {
    $description.setAttribute('content', meta.description);
  }
  if ($keywords) {
    $keywords.setAttribute('content', meta.keywords);
  }
};

const cmsRoutes = window.ioappConfig.routes.map((route) => ({
  name: route.name,
  path: route.path,
  meta: route.meta,
  component: BasePage,
}));

const routes = [
  ...cmsRoutes,
  {
    path: '/suche/',
    meta: {
      type: 'search',
      title: 'Suche',
    },
    component: () => import(/* webpackChunkName: "chunks/search-page" */ '@/components/Search.vue'),
    name: 'search',
  },
  {
    path: '/preisanzeige/:branch/:postcode/:usage/',
    component: Priceshow,
    meta: {
      type: 'priceshow',
      title: 'Ihr persönliches Angebot',
    },
    name: 'priceshow.single',
  },
  {
    path: '/preisanzeige/:branch/:postcode/:usage/:usageHt/',
    component: Priceshow,
    meta: {
      type: 'priceshow',
      title: 'Ihr persönliches Angebot',
    },
    name: 'priceshow.double',
  },
  {
    path: '/warenkorb/',
    meta: {
      type: 'cart',
      title: 'Ihr Wahrenkorb',
    },
    name: 'cart',
    component: () => import(/* webpackChunkName: "chunks/cart" */ '@/pages/CartPage.vue'),
  },
  {
    path: '/bestellung/adresse/',
    meta: {
      type: 'order',
      title: 'Bestellung - Adresse',
    },
    name: 'order.address',
    component: () => import(/* webpackChunkName: "chunks/order-pages" */ '@/pages/OrderPage.vue'),
    props: { step: 'address' },
  },
  {
    path: '/bestellung/anschlussdaten/',
    meta: {
      type: 'order',
      title: 'Bestellung - Anschlussdaten',
    },
    name: 'order.connection',
    component: () => import(/* webpackChunkName: "chunks/order-pages" */ '@/pages/OrderPage.vue'),
    props: { step: 'connection' },
  },
  {
    path: '/bestellung/zahlungsweise/',
    meta: {
      type: 'order',
      title: 'Bestellung - Zahlungsweise',
    },
    name: 'order.payment',
    component: () => import(/* webpackChunkName: "chunks/order-pages" */ '@/pages/OrderPage.vue'),
    props: { step: 'payment' },
  },
  {
    path: '/bestellung/uebersicht/',
    meta: {
      type: 'order',
      title: 'Bestellung - Übersicht',
    },
    name: 'order.overview',
    component: () => import(/* webpackChunkName: "chunks/order-pages" */ '@/pages/OrderPage.vue'),
    props: { step: 'overview' },
  },
  {
    path: '/bestellung/erfolgreich/',
    meta: {
      type: 'order-success',
      title: 'Bestellung - Erfolgreich',
    },
    name: 'order.success',
    component: () => import(/* webpackChunkName: "chunks/order-pages" */ '@/pages/OrderSuccessPage.vue'),
  },
  {
    path: '/bestellung/aktivieren/:hash',
    meta: {
      type: 'order-activation',
      title: 'Bestellung - Aktivieren',
    },
    name: 'order.activation',
    component: () => import(/* webpackChunkName: "chunks/order-pages" */ '@/pages/OrderActivationPage.vue'),
  },
  {
    path: '/bestellung/fortsetzen/:token',
    meta: {
      type: 'order-continue',
      title: 'Bestellung - Fortsetzen',
    },
    name: 'order.continue',
    component: () => import(/* webpackChunkName: "chunks/order-pages" */ '@/pages/OrderContinuePage.vue'),
  },
  {
    path: '/:catchAll(.*)',
    component: BasePage,
  },
];

const router = createRouter({
  history: createWebHistory('/'),
  routes,
  scrollBehavior(to) {
    if (to.hash) {
      return {
        el: to.hash,
        behavior: 'smooth',
      };
    }

    return {
      top: 0,
    };
  },
});

router.beforeResolve(async (to, from, next) => {
  if (
    !to.path.endsWith('/')
    && to.meta.type !== 'order-activation'
    && to.meta.type !== 'order-continue'
  ) {
    const newLocation = to;
    newLocation.path = `${to.path}/`;

    next(newLocation);

    return;
  }

  store.dispatch('page/setPageType', to.meta.type);

  if ((from.name === undefined || from.name === 'order.success')
    && to.name !== 'order.success') {
    store.dispatch('order/deleteLastOrderedItem');
  }

  if (['order', 'priceshow'].includes(to.meta.type as string)) {
    updateOrderPageMeta(to.meta as Record<string, string>);
  }

  switch (to.meta.type) {
    case 'search':
      beforeSearchRouteEnter();
      next();

      return;
    case 'order':
      await beforeOrderRouteEnter().then((canEnter) => {
        if (canEnter) {
          next();
        } else {
          next('/');
        }
      });

      return;
    case 'order-success':
      await beforeOrderSuccessRouteEnter().then((canEnter) => {
        if (canEnter) {
          next();
        } else {
          next('/');
        }
      });

      return;
    default:
      next();
  }
});

export default router;
